import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus } from "lucide-react";
import { useState } from "react";
import type {
  Project,
  Task,
  TaskComment,
  TaskEvent,
  TaskLink,
  TaskLinkTarget,
  TaskWithDetails,
  UserSummary,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DatePicker } from "@/components/dates/DatePicker";
import { cn } from "@/lib/utils";
import { DescriptionEditor } from "@/modules/tasks/components/DescriptionEditor";
import { StatusPill } from "@/components/table/StatusPill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  projectLabel,
  userLabel,
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";
import {
  isTaskPriority,
  isTaskStatus,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "@/modules/tasks/task-enums";
import { CommentComposer } from "@/modules/comments/CommentComposer";
import { MentionedText } from "@/modules/tasks/components/MentionedText";
import { ParentTaskLine } from "@/modules/tasks/components/ParentTaskLine";
import { AttachmentsCard } from "@/modules/tasks/components/TaskAttachments";
import { useTaskAttachments } from "@/modules/tasks/components/useTaskAttachments";
import { TaskForm } from "@/modules/tasks/screens/TaskForm";
import {
  eventValue,
  namesAUser,
  sortEventsNewestFirst,
  taskEventLabel,
} from "@/modules/tasks/timeline";
import { describeRejection } from "@/modules/tasks/writes";

const LINK_TARGET_LABEL: Record<TaskLinkTarget, string> = {
  order: "Order",
  contact: "Contact",
  transfer: "Transfer",
  invoice: "Invoice",
  product: "Product",
};

/** One link, resolved to what it points at rather than left as a bare id (FR-TSK-10). */
function LinkRow({ link }: { link: TaskLink }) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["tasks", "link-target", link.targetType, link.targetId],
    queryFn: async (): Promise<string | null> => {
      switch (link.targetType) {
        case "order": {
          const order = await data.orders.get(link.targetId);
          return order ? order.orderNumber : null;
        }
        case "contact": {
          const contact = await data.contacts.get(link.targetId);
          return contact ? contact.displayName : null;
        }
        case "transfer": {
          const transfer = await data.warehouses.getTransfer(link.targetId);
          return transfer ? transfer.reference : null;
        }
        case "invoice": {
          const invoice = await data.invoicing.get(link.targetId);
          return invoice ? (invoice.invoiceNumber ?? "Draft") : null;
        }
        case "product": {
          const product = await data.catalog.getProduct(link.targetId);
          return product ? product.title : null;
        }
      }
    },
  });

  const label = query.isPending
    ? "Loading…"
    : query.isError
      ? link.targetId
      : (query.data ?? `Not found (${link.targetId})`);

  return (
    <li className="flex items-center gap-2 text-sm">
      <Badge variant="outline" className="font-normal">
        {LINK_TARGET_LABEL[link.targetType]}
      </Badge>
      <span>{label}</span>
    </li>
  );
}

function LinksCard({ links }: { links: TaskLink[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Links</CardTitle>
      </CardHeader>
      <CardContent>
        {links.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing linked to this task.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {links.map((link) => (
              <LinkRow key={link.id} link={link} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * FR-TSK-6: a parent shows its subtasks' completion state. Checking one off
 * calls the same `TasksRepository.update` the status control above it does
 * — a subtask is a `Task` with its own id, not a special shape — toggling
 * between `todo` and `done` rather than offering the full status list,
 * because "is this piece done" is the only question a checkbox can ask.
 */
function SubtasksCard({
  subtasks,
  users,
  onToggle,
  togglingId,
}: {
  subtasks: Task[];
  users: UserSummary[];
  onToggle: (subtask: Task) => void;
  togglingId: string | null;
}) {
  const done = subtasks.filter((subtask) => subtask.status === "done").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          Subtasks
          {subtasks.length > 0 ? (
            <span className="text-muted-foreground ml-2 font-normal">
              {done} of {subtasks.length} complete
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {subtasks.length === 0 ? (
          <p className="text-muted-foreground text-sm">No subtasks.</p>
        ) : (
          <ul className="space-y-2">
            {subtasks.map((subtask) => (
              <li key={subtask.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={subtask.status === "done"}
                  disabled={togglingId === subtask.id}
                  onCheckedChange={() => {
                    onToggle(subtask);
                  }}
                />
                <span
                  className={
                    subtask.status === "done"
                      ? "text-muted-foreground line-through"
                      : ""
                  }
                >
                  {subtask.title}
                </span>
                <span className="text-muted-foreground ml-auto text-xs">
                  {subtask.assigneeUserId
                    ? userLabel(users, subtask.assigneeUserId)
                    : "Unassigned"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** FR-TSK-8: an edited comment says so, and a soft-deleted one leaves a placeholder rather than a gap. */
function CommentsCard({
  comments,
  users,
  onPost,
}: {
  comments: TaskComment[];
  users: UserSummary[];
  /** The refusal to show, or `null` when the comment was accepted. */
  onPost: (body: string) => Promise<WriteRejection | null>;
}) {
  const ordered = [...comments].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : 1,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Comments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {ordered.length === 0 ? (
          <p className="text-muted-foreground text-sm">No comments yet.</p>
        ) : (
          <ul className="space-y-3">
            {ordered.map((comment) => (
              <li key={comment.id} className="border-b pb-3 last:border-b-0">
                {comment.deletedAt !== null ? (
                  <p className="text-muted-foreground text-sm italic">
                    Comment removed.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium">
                        {userLabel(users, comment.authorUserId)}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {comment.createdAt}
                        {comment.editedAt !== null ? " (edited)" : ""}
                      </span>
                    </div>
                    {/* Markdown, stored as written (see `Task.description` in
                        @estelle/domain) — rendered as preformatted text
                        rather than through a Markdown renderer: no such
                        dependency is installed for this ticket, and showing
                        exactly what was typed is honest where a half-parsed
                        renderer would not be. */}
                    <pre className="mt-1 font-sans text-sm break-words whitespace-pre-wrap">
                      <MentionedText body={comment.body} users={users} />
                    </pre>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {/*
          The comment box is the one the order and customer threads use (#448):
          `@` offers active people, and picking one carries that person's id
          into the draft — so two people with one name are two mentions. What is
          sent has `@[<userId>]` tokens, which the server reads to notify them.
        */}
        <div className="space-y-1.5 pt-1">
          <p className="text-sm leading-none font-medium">Add a comment</p>
          <CommentComposer
            people={users}
            allowFiles={false}
            label="Write a comment"
            submitLabel="Comment"
            placeholder="Write a comment. Type @ to mention someone."
            submit={({ body }) => onPost(body)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/** FR-TSK-9: every change, append-only, timestamped and attributed. */
function TimelineCard({
  events,
  users,
}: {
  events: TaskEvent[];
  users: UserSummary[];
}) {
  const ordered = sortEventsNewestFirst(events);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {ordered.length === 0 ? (
          <p className="text-muted-foreground text-sm">No recorded activity.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="py-2 pr-3 font-normal">When</th>
                  <th className="py-2 pr-3 font-normal">Change</th>
                  <th className="py-2 pr-3 font-normal">Field</th>
                  <th className="py-2 pr-3 font-normal">Before</th>
                  <th className="py-2 pr-3 font-normal">After</th>
                  <th className="py-2 font-normal">By</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((event) => (
                  <tr key={event.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                      {event.occurredAt}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="font-normal">
                        {taskEventLabel(event.eventType)}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground py-2 pr-3">
                      {event.field ?? "—"}
                    </td>
                    <td className="py-2 pr-3">
                      {namesAUser(event.eventType) &&
                      typeof event.before === "string"
                        ? userLabel(users, event.before)
                        : eventValue(event.before)}
                    </td>
                    <td className="py-2 pr-3">
                      {namesAUser(event.eventType) &&
                      typeof event.after === "string"
                        ? userLabel(users, event.after)
                        : eventValue(event.after)}
                    </td>
                    <td className="py-2">
                      {userLabel(users, event.actorUserId)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface UpdatePatch {
  status?: Task["status"];
  priority?: Task["priority"];
  assigneeUserId?: string | null;
  dueDate?: string | null;
}

/**
 * FR-TSK-6: closing a task with an open subtask comes back as a `blocked`
 * rejection, and the only way past it is to say why. `TasksRepository.update`
 * carries the reason as a third, optional argument added for exactly this —
 * see its own comment in `@estelle/domain` — so this is the one place in the
 * screen that ever passes it.
 */
function OverrideBanner({
  rejection,
  onOverride,
  pending,
}: {
  rejection: Extract<WriteRejection, { kind: "blocked" }>;
  onOverride: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className="border-destructive/40 bg-destructive/5 space-y-2 rounded-md border p-3 sm:col-span-2">
      <p className="text-destructive text-sm">{describeRejection(rejection)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          placeholder="Reason for closing anyway"
          className="max-w-xs"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={reason.trim() === "" || pending}
          onClick={() => {
            onOverride(reason.trim());
          }}
        >
          Close anyway
        </Button>
      </div>
    </div>
  );
}

function DetailsCard({
  task,
  users,
  onUpdate,
  rejection,
  onOverride,
  overridePending,
}: {
  task: Task;
  users: UserSummary[];
  onUpdate: (patch: UpdatePatch) => void;
  rejection: WriteRejection | null;
  onOverride: (reason: string) => void;
  overridePending: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Details</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select
            value={task.status}
            onValueChange={(value) => {
              if (!isTaskStatus(value)) return;
              onUpdate({ status: value });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <StatusPill value={option.value} label={option.label} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Priority</Label>
          <Select
            value={task.priority}
            onValueChange={(value) => {
              if (!isTaskPriority(value)) return;
              onUpdate({ priority: value });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_PRIORITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <StatusPill value={option.value} label={option.label} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Assignee</Label>
          <Select
            value={task.assigneeUserId ?? "unassigned"}
            onValueChange={(value) => {
              onUpdate({
                assigneeUserId: value === "unassigned" ? null : value,
              });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="task-due-date">Due date</Label>
          <DatePicker
            id="task-due-date"
            value={task.dueDate ?? ""}
            onChange={(value) => {
              onUpdate({ dueDate: value === "" ? null : value });
            }}
          />
        </div>

        {rejection ? (
          rejection.kind === "blocked" ? (
            <OverrideBanner
              rejection={rejection}
              onOverride={onOverride}
              pending={overridePending}
            />
          ) : (
            <p className="text-destructive text-sm sm:col-span-2">
              {describeRejection(rejection)}
            </p>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

function TaskDetailBody({
  task,
  users,
  projects,
  onUpdate,
  onSaveDescription,
  updateRejection,
  onOverride,
  overridePending,
  onToggleSubtask,
  togglingSubtaskId,
  onDelete,
  deletePending,
  deleteRejection,
  onPostComment,
}: {
  task: TaskWithDetails;
  users: UserSummary[];
  projects: Project[];
  onUpdate: (patch: UpdatePatch) => void;
  onSaveDescription: (
    description: string | null,
  ) => Promise<WriteRejection | null>;
  updateRejection: WriteRejection | null;
  onOverride: (reason: string) => void;
  overridePending: boolean;
  onToggleSubtask: (subtask: Task) => void;
  togglingSubtaskId: string | null;
  onDelete: () => void;
  deletePending: boolean;
  deleteRejection: WriteRejection | null;
  onPostComment: (body: string) => Promise<WriteRejection | null>;
}) {
  const permissions = usePermissions();
  const [addingSubtask, setAddingSubtask] = useState(false);
  // FR-TSK-5: one level of nesting — a subtask cannot itself gain a subtask,
  // so the action that would try is hidden on one rather than surfacing a
  // rejection the moment someone opens the form.
  const canAddSubtask =
    can(permissions, "tasks.task.create") &&
    task.parentTaskId === null &&
    task.deletedAt === null;
  // #625: files go on through the Attach button, a drop anywhere on the task,
  // or a pasted screenshot.
  const attachments = useTaskAttachments(
    task.id,
    can(permissions, "tasks.task.edit") && task.deletedAt === null,
  );

  return (
    <div
      className={cn(
        "max-w-3xl space-y-4 rounded-lg",
        attachments.dragOver && "ring-primary ring-2 ring-offset-4",
      )}
      {...attachments.dropProps()}
    >
      <header className="space-y-2">
        {task.parentTaskId === null ? null : (
          <ParentTaskLine parentTaskId={task.parentTaskId} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{task.reference}</Badge>
            <h2 className="text-lg font-semibold">{task.title}</h2>
            {task.deletedAt !== null ? (
              <Badge variant="secondary">Deleted</Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {canAddSubtask ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAddingSubtask(true);
                }}
              >
                <Plus />
                Add subtask
              </Button>
            ) : null}
            {task.deletedAt === null ? (
              <Button
                size="sm"
                variant="outline"
                disabled={deletePending}
                onClick={onDelete}
              >
                Delete task
              </Button>
            ) : null}
          </div>
        </div>
        <TaskForm
          key={task.id}
          open={addingSubtask}
          onOpenChange={setAddingSubtask}
          parentTask={task}
          onCreated={() => {
            setAddingSubtask(false);
          }}
        />
        <p className="text-muted-foreground text-sm">
          {projectLabel(projects, task.projectId)} · reported by{" "}
          {userLabel(users, task.reporterUserId)}
        </p>
        {deleteRejection ? (
          <p className="text-destructive text-sm">
            {describeRejection(deleteRejection)}
          </p>
        ) : null}
        {task.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {task.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}
        {task.watcherUserIds.length > 0 ? (
          <p className="text-muted-foreground text-xs">
            Watched by{" "}
            {task.watcherUserIds.map((id) => userLabel(users, id)).join(", ")}
          </p>
        ) : null}
      </header>

      <DescriptionEditor
        key={task.id}
        description={task.description}
        canEdit={can(permissions, "tasks.task.edit") && task.deletedAt === null}
        onSave={onSaveDescription}
      />

      <DetailsCard
        task={task}
        users={users}
        onUpdate={onUpdate}
        rejection={updateRejection}
        onOverride={onOverride}
        overridePending={overridePending}
      />

      <SubtasksCard
        subtasks={task.subtasks}
        users={users}
        onToggle={onToggleSubtask}
        togglingId={togglingSubtaskId}
      />

      <CommentsCard
        comments={task.comments}
        users={users}
        onPost={onPostComment}
      />

      <LinksCard links={task.links} />

      <AttachmentsCard
        attachments={task.attachments}
        users={users}
        state={attachments}
        taskId={task.id}
      />

      <TimelineCard events={task.events} users={users} />
    </div>
  );
}

/**
 * One task, with everything that lives only on `TaskWithDetails` — `get()`
 * is fetched here rather than accepted as a prop, the same reason
 * `ContactDetail` fetches its own record: subtasks, comments, links,
 * attachments and the timeline never travel with a list row.
 *
 * There is no `/tasks/:id` route — routes come from `config/modules.ts`,
 * which this module cannot edit, and a task is not one of its sections.
 * `TaskList` and `TaskBoard` both render this in place of their own rows,
 * and `onBack` is how it hands control back to whichever called it.
 */
export function TaskDetail({
  taskId,
  onBack,
}: {
  taskId: string;
  onBack?: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const detailKey = ["tasks", "detail", taskId];

  const taskQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => data.tasks.get(taskId),
  });
  const usersQuery = useUserDirectory();
  const projectsQuery = useProjectDirectory();

  const [updateRejection, setUpdateRejection] = useState<WriteRejection | null>(
    null,
  );
  const [togglingSubtaskId, setTogglingSubtaskId] = useState<string | null>(
    null,
  );
  // FR-TSK-6: remembered so "Close anyway" can resend the same status change
  // with a reason once the first attempt comes back `blocked`.
  const [lastStatusAttempt, setLastStatusAttempt] = useState<
    Task["status"] | null
  >(null);

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      patch,
      overrideReason,
    }: {
      id: string;
      patch: UpdatePatch;
      overrideReason?: string;
    }) =>
      data.tasks.update(
        id,
        patch,
        overrideReason ? { overrideReason } : undefined,
      ),
    onSuccess: (result) => {
      setTogglingSubtaskId(null);
      if (result.ok) {
        setUpdateRejection(null);
        setLastStatusAttempt(null);
        // Broad rather than just `detailKey`: a subtask toggle changes a row
        // `TaskList`/`TaskBoard` may already have cached, and a status edit
        // here should not need a manual refresh to show up back there.
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        // Closing a task can clear an order's "Has open follow-up" (#445).
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
      } else {
        setUpdateRejection(result.rejection);
      }
    },
  });

  // The description editor owns its draft and shows its own refusal inline, so
  // it gets a mutation of its own rather than sharing `updateRejection` with
  // the Details card (#626).
  const descriptionMutation = useMutation({
    mutationFn: (description: string | null) =>
      data.tasks.update(taskId, { description }),
    onSuccess: (result) => {
      if (result.ok) {
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      }
    },
  });

  const [deleteRejection, setDeleteRejection] = useState<WriteRejection | null>(
    null,
  );
  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.tasks.delete(id),
    onSuccess: (result) => {
      if (result.ok) {
        setDeleteRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        // So does deleting one (#445).
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
      } else {
        setDeleteRejection(result.rejection);
      }
    },
  });

  // The composer owns the draft, what is pending and the last refusal; it asks
  // only for the outcome, a refusal to show or `null` once the comment is in.
  const commentMutation = useMutation({
    mutationFn: (body: string) => data.tasks.comment(taskId, body),
    onSuccess: (result) => {
      if (result.ok) {
        void queryClient.invalidateQueries({ queryKey: detailKey });
      }
    },
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Optional: the drawer this renders inside carries its own close, and two
        ways out of one panel side by side each look like they do something
        different.
      */}
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to list
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {taskQuery.isPending ||
        usersQuery.isPending ||
        projectsQuery.isPending ? (
          <TableSkeleton rows={6} />
        ) : taskQuery.isError ? (
          <LoadFailed
            message={taskQuery.error.message}
            onRetry={() => {
              void taskQuery.refetch();
            }}
          />
        ) : usersQuery.isError ? (
          <LoadFailed
            message={usersQuery.error.message}
            onRetry={() => {
              void usersQuery.refetch();
            }}
          />
        ) : projectsQuery.isError ? (
          <LoadFailed
            message={projectsQuery.error.message}
            onRetry={() => {
              void projectsQuery.refetch();
            }}
          />
        ) : taskQuery.data === null ? (
          <EmptyState
            title="Task not found"
            description="It may have been deleted since this list was loaded."
          />
        ) : (
          <TaskDetailBody
            task={taskQuery.data}
            users={usersQuery.data.items}
            projects={projectsQuery.data.items}
            onUpdate={(patch) => {
              setLastStatusAttempt(patch.status ?? null);
              updateMutation.mutate({ id: taskId, patch });
            }}
            onSaveDescription={async (description) => {
              const result = await descriptionMutation.mutateAsync(description);
              return result.ok ? null : result.rejection;
            }}
            updateRejection={updateRejection}
            onOverride={(reason) => {
              if (lastStatusAttempt === null) return;
              updateMutation.mutate({
                id: taskId,
                patch: { status: lastStatusAttempt },
                overrideReason: reason,
              });
            }}
            overridePending={updateMutation.isPending}
            onToggleSubtask={(subtask) => {
              setTogglingSubtaskId(subtask.id);
              updateMutation.mutate({
                id: subtask.id,
                patch: {
                  status: subtask.status === "done" ? "todo" : "done",
                },
              });
            }}
            togglingSubtaskId={togglingSubtaskId}
            onDelete={() => {
              deleteMutation.mutate(taskId);
            }}
            deletePending={deleteMutation.isPending}
            deleteRejection={deleteRejection}
            onPostComment={async (body) => {
              const result = await commentMutation.mutateAsync(body);
              return result.ok ? null : result.rejection;
            }}
          />
        )}
      </div>
    </div>
  );
}
